import { FC } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { customerZ } from '@libs/schemas';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PhoneInput } from 'components/ui/PhoneInput';
import { ErrorMessage } from '@hookform/error-message';

/**
 * One definition of what a new customer is, shared by every surface that creates one: the
 * customers dashboard sheet and the create-and-assign subview inside the order customer
 * drawer. Two copies of these fields meant two sets of validation rules to keep in step.
 */
export const customerFormZ = customerZ.extend({
  email: z.email('Invalid email address').min(1, 'Email is required'),
  company: z
    .object({
      name: z.string().min(1).optional(),
      email: z.email('Invalid email address').optional().or(z.literal('')),
    })
    .optional(),
});

export type CustomerFormData = z.infer<typeof customerFormZ>;

export const customerFormDefaults: CustomerFormData = {
  name: { first: '', last: '' },
  email: '',
  phone: '',
  company: undefined,
  smsNotificationOptIn: true,
} as CustomerFormData;

interface CustomerFormFieldsProps {
  form: UseFormReturn<CustomerFormData>;
}

/**
 * The fields only. Callers own the surrounding `<Form>`/`<form>` and their own action row, so
 * a sheet with a sticky footer and a drawer subview with inline actions can share these
 * inputs without inheriting each other's layout.
 */
export const CustomerFormFields: FC<CustomerFormFieldsProps> = ({ form }) => (
  <>
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Customer Details</h3>
        <p className="text-sm text-muted">Basic customer information</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="name.first"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-muted">First Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name.last"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-muted">Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-muted">Email</FormLabel>
              <FormControl>
                <Input placeholder="Enter email address" type="email" {...field} />
              </FormControl>
              <ErrorMessage errors={form.formState.errors} name="email" />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-muted">Phone</FormLabel>
              <FormControl>
                <PhoneInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>

    <Separator />

    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>
        <p className="text-sm text-muted">Optional company details</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <FormField
          control={form.control}
          name="company.name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-muted">
                Company Name (optional)
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter company name" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="company.email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-muted">
                Company Email (optional)
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter company email address"
                  type="email"
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === '' ? undefined : value);
                  }}
                />
              </FormControl>
              <ErrorMessage errors={form.formState.errors} name="company.email" />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  </>
);

/** Company is only sent when the admin actually filled one of its fields in. */
export const toCreateCustomerInput = (data: CustomerFormData) => ({
  ...data,
  company:
    data.company?.name || data.company?.email
      ? { name: data.company?.name || undefined, email: data.company?.email || undefined }
      : undefined,
});
